/**
 * What a session check actually told us — the three answers both clients must
 * keep apart.
 *
 * Better Auth's session store reports a failed `get-session` the same way as a
 * signed-out visitor: `data: null`. Only `error` tells them apart. A 401 is the
 * server saying "no session"; anything else — a fetch that never reached the
 * server (offline, DNS, the auth worker down), a 5xx, a timeout — means we
 * could not ASK, and the visitor may well be signed in.
 *
 * Collapsing `unknown` into `signed-out` is how an offline reload of a deck the
 * user owns ended on "This deck does not exist": the page took the failed
 * check for a signed-out visitor and went looking for a public copy, which an
 * offline fetch could not find either.
 *
 * Pure, and free of better-auth types: the store's shape is passed in.
 */
export type SessionCheck = 'signed-in' | 'signed-out' | 'unknown';

export function sessionCheck({
  hasUser,
  error,
}: {
  /** The session store holds a user — current, or kept from before a failed refetch. */
  hasUser: boolean;
  /**
   * The store's last error, if the most recent session fetch failed — a
   * better-fetch error (`{ status }`) or whatever a failed `fetch` threw.
   */
  error: unknown;
}): SessionCheck {
  // A user in hand wins: Better Auth keeps the last good session through a
  // failed refetch, and so does the Expo client's on-device cache.
  if (hasUser) return 'signed-in';
  if (!error) return 'signed-out';
  // The server answered "no session" — definitive.
  const status =
    typeof error === 'object' && 'status' in error
      ? (error as { status?: unknown }).status
      : undefined;
  if (status === 401) return 'signed-out';
  return 'unknown';
}
